import { max } from 'lodash'
import { GetThreadPreviewTextParams } from './interfaces/getThreadPreviewTextParams.interface'
import {
  addThreeDotsToString,
  addThreeDotsToStringStart,
} from '../addThreeDotsToString'

const MAX_CHARACTERS_IN_PREVIEW = 51

const DOTS_LENGTH = 3

export const getThreadPreviewText = ({
  text,
  keyword,
}: GetThreadPreviewTextParams): string => {
  if (!text) {
    return ''
  }

  const trimmedKeyword = keyword?.trim()

  // Check if whole text fits in preview.
  if (text.length <= MAX_CHARACTERS_IN_PREVIEW || !trimmedKeyword) {
    return addThreeDotsToString(text, MAX_CHARACTERS_IN_PREVIEW)
  }

  // Split text into two parts, before and after keyword.

  const indexOfFirstKeyword = text
    .toLowerCase()
    .indexOf(trimmedKeyword.toLowerCase())

  // Keyword may not appear verbatim in the text (e.g. inflected forms in
  // Georgian) — fall back to the start of the text instead of an empty preview.
  if (indexOfFirstKeyword === -1) {
    return addThreeDotsToString(text, MAX_CHARACTERS_IN_PREVIEW)
  }

  const fullLeftText = text.slice(0, indexOfFirstKeyword)
  const keywordInText = text.slice(
    indexOfFirstKeyword,
    indexOfFirstKeyword + trimmedKeyword.length
  )
  const fullRightText = text.slice(indexOfFirstKeyword + trimmedKeyword.length)

  // Check if (fullLeftText + keyword + dots) fits in preview.
  // Dots need to be added at the end, because whole text doesn't fit.

  const paddingCharactersCount =
    MAX_CHARACTERS_IN_PREVIEW - DOTS_LENGTH - trimmedKeyword.length

  if (fullLeftText.length < paddingCharactersCount) {
    return addThreeDotsToString(text, MAX_CHARACTERS_IN_PREVIEW)
  }

  // \S/\s instead of \w/\b: \w only matches Latin characters, which breaks
  // word splitting for non-Latin scripts (Georgian, Cyrillic, etc.).
  const fullLeftTextParts = fullLeftText.match(/\S+\s+/g) || []
  let leftText = ''

  const leftPaddingCharactersCountMin = paddingCharactersCount / 2
  const leftPaddingCharactersCount =
    max([
      leftPaddingCharactersCountMin,
      MAX_CHARACTERS_IN_PREVIEW -
        fullRightText.length -
        keywordInText.length -
        DOTS_LENGTH,
    ]) || leftPaddingCharactersCountMin

  if (fullLeftTextParts.length < 2) {
    leftText = addThreeDotsToStringStart(
      fullLeftTextParts[0] || '',
      leftPaddingCharactersCount
    )
  } else {
    let leftTextWithoutDots = ''
    let index = fullLeftTextParts.length - 1

    while (
      index >= 0 &&
      (fullLeftTextParts[index] + leftTextWithoutDots).length <
        leftPaddingCharactersCount
    ) {
      leftTextWithoutDots = fullLeftTextParts[index] + leftTextWithoutDots
      index--
    }

    // If full left text doesn't fit, add dots at the start.
    leftText = `${index !== -1 ? '...' : ''}${leftTextWithoutDots}`
  }

  const previewText =
    leftPaddingCharactersCountMin === leftPaddingCharactersCount
      ? leftText + keywordInText + fullRightText
      : addThreeDotsToString(
          leftText + keywordInText + fullRightText,
          MAX_CHARACTERS_IN_PREVIEW
        )

  return previewText
}
